import { createCeoOrchestratorPlan } from "./ceoOrchestratorPlan.js";
const DEFAULT_ALLOWED_PREVIEW_ACTIONS = [
    "classify_intent",
    "preview_route",
    "explain",
    "validate",
    "read_metadata",
];
const actionId = (...parts) => parts.join("_");
const DEFAULT_BLOCKED_ACTIONS = [
    "direct_production_execute",
    "connector_runtime_execute",
    actionId("ssh", "execute"),
    actionId("shell", "execute"),
    actionId("deploy", "execute"),
    "payment_trigger",
    "voucher_posting",
    actionId("secret", "read"),
    "secret_mutation",
    "database_mutation",
    "external_send",
];
const DEFAULT_APPROVAL_REQUIRED_ACTIONS = [
    "server_action_request",
    "connector_dispatch_request",
    "dual_run_start",
    "production_verification",
    "product_app_mutation",
];
function routeKindForPlan(plan, taskText) {
    const text = taskText.toLowerCase();
    if (plan.policy === "blocked" || plan.risk === "critical")
        return "blocked_sensitive_action";
    if (text.includes("desktop") || text.includes("electron") || text.includes("local preview"))
        return "desktop_preview";
    if (text.includes("architecture") || text.includes("roadmap") || text.includes("system design"))
        return "architecture_review";
    if (plan.intent === "deployment" || plan.intent === "backup" || plan.intent === "vps_check")
        return "server_action_preview";
    if (plan.intent === "code_change")
        return "code_implementation";
    if (plan.intent === "architecture_plan")
        return "architecture_review";
    if (plan.intent === "desktop_ui")
        return "desktop_preview";
    if (plan.intent === "agent_management" || plan.intent === "workflow_design" || plan.intent === "browser_automation") {
        return "agentops_planning";
    }
    if (plan.intent === "app_support")
        return "product_dual_run_planning";
    return "manual_review";
}
function statusForPlan(plan) {
    if (plan.policy === "blocked" || plan.risk === "critical")
        return "blocked";
    if (plan.policy === "approval_required")
        return "approval_required";
    if (plan.policy === "read_only" || plan.policy === "dry_run")
        return "preview_ready";
    return "metadata_only";
}
function nextSafeStepForRoute(routeKind, status) {
    if (status === "blocked")
        return "manual_safety_review_required";
    if (status === "approval_required")
        return "create_approval_request_preview";
    if (routeKind === "product_dual_run_planning")
        return "prepare_product_dual_run_mapping";
    if (routeKind === "desktop_preview")
        return "render_metadata_preview_only";
    if (routeKind === "server_action_preview")
        return "create_server_action_plan_without_execution";
    if (routeKind === "code_implementation")
        return "prepare_code_pr_plan";
    if (routeKind === "architecture_review")
        return "prepare_architecture_review_plan";
    if (routeKind === "agentops_planning")
        return "prepare_agentops_assignment_preview";
    return "manual_review_preview";
}
export function createCeoWorkflowRoutingPolicy() {
    return {
        policyId: "ceo-workflow-routing-policy",
        generatedAt: new Date(0).toISOString(),
        defaultStatus: "metadata_only",
        supportedRouteKinds: [
            "product_preview",
            "product_dual_run_planning",
            "code_implementation",
            "architecture_review",
            "desktop_preview",
            "agentops_planning",
            "server_action_preview",
            "blocked_sensitive_action",
            "manual_review",
        ],
        blockedActions: DEFAULT_BLOCKED_ACTIONS,
        approvalRequiredActions: DEFAULT_APPROVAL_REQUIRED_ACTIONS,
        allowedPreviewActions: DEFAULT_ALLOWED_PREVIEW_ACTIONS,
        safetyNotesHindi: [
            "CEO workflow routing policy metadata-only planning layer hai.",
            "Yeh policy real connector, network, SSH, deploy, payment ya product app mutation execute nahi karti.",
            "High-risk route approval_required rahega aur critical route blocked rahega.",
        ],
        metadata: {
            metadataOnly: true,
            realExecution: false,
            networkCall: false,
            productMutation: false,
        },
    };
}
export function routeCeoWorkflowTask(input) {
    const plan = createCeoOrchestratorPlan(input);
    const policy = createCeoWorkflowRoutingPolicy();
    const routeKind = routeKindForPlan(plan, input.taskText);
    const status = statusForPlan(plan);
    const blockedActions = Array.from(new Set([...policy.blockedActions, ...plan.blockedActions]));
    const approvalRequiredActions = status === "approval_required"
        ? policy.approvalRequiredActions
        : status === "blocked"
            ? [...policy.approvalRequiredActions, "manual_safety_override_review"]
            : [];
    return {
        routeId: `ceo-route:${plan.taskId}`,
        routeKind,
        status,
        intent: plan.intent,
        risk: plan.risk,
        policy: plan.policy,
        targetAppRefs: plan.recommendedAppRefs,
        targetServiceRefs: plan.recommendedServiceRefs,
        targetAgentRefs: plan.recommendedAgentRefs,
        allowedPreviewActions: policy.allowedPreviewActions,
        blockedActions,
        approvalRequiredActions,
        nextSafeStep: nextSafeStepForRoute(routeKind, status),
        warnings: plan.warnings,
        metadata: {
            metadataOnly: true,
            realExecution: false,
            sourcePlanId: plan.taskId,
            requestedRoute: input.currentRoute || null,
            inputMetadata: input.metadata || {},
        },
    };
}
export function listCeoWorkflowRoutingBlockers(decision) {
    const blockers = [];
    if (decision.status === "blocked") {
        blockers.push("Route is blocked until manual safety review.");
    }
    if (decision.blockedActions.length > 0) {
        blockers.push(...decision.blockedActions.map((action) => `Blocked action: ${action}`));
    }
    return blockers;
}
export function listCeoWorkflowRoutingWarnings(decision) {
    const warnings = [...decision.warnings];
    if (decision.status === "approval_required") {
        warnings.push("Manual approval is required before any connector or production action.");
    }
    if (decision.status === "metadata_only") {
        warnings.push("Route is metadata-only; use preview and planning actions only.");
    }
    return warnings;
}
export function explainCeoWorkflowRoutingHindi(decision) {
    return [
        `CEO workflow route: ${decision.routeKind}`,
        `स्थिति: ${decision.status}`,
        `intent: ${decision.intent}`,
        `risk: ${decision.risk}`,
        `policy: ${decision.policy}`,
        `next safe step: ${decision.nextSafeStep}`,
        "यह routing policy सिर्फ metadata-only planning है; कोई real execution, SSH, deploy, payment, network या product mutation नहीं किया गया।",
    ].join("\n");
}
//# sourceMappingURL=workflowRoutingPolicy.js.map